import { useEffect, useRef, useState } from "react";
import { Empty, ErrorState, Loading, cx } from "@/components/ui";
import { useMarkRead, useMessages, useSendMessage, uploadFiles } from "@/lib/queries";
import { useSession } from "@/hooks/useSession";
import { MessageBubble } from "./MessageBubble";

/** Fil d'une conversation : messages (rafraîchis toutes les 5 s) + marquage lu. */
export function ThreadMessages({ conversationId, compact = false }: { conversationId: string; compact?: boolean }) {
  const q = useMessages(conversationId);
  const markRead = useMarkRead();
  const endRef = useRef<HTMLDivElement>(null);
  const lastId = q.data?.messages.at(-1)?.id;

  useEffect(() => {
    if (lastId) {
      markRead.mutate(conversationId);
      endRef.current?.scrollIntoView({ block: "end" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, lastId]);

  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorState error={q.error} />;
  const msgs = q.data?.messages ?? [];
  if (msgs.length === 0) return <Empty>Aucun message pour le moment.</Empty>;
  return (
    <div className={cx("flex flex-col", compact ? "gap-3" : "gap-4")}>
      {msgs.map((m) => (
        <MessageBubble key={m.id} m={m} compact={compact} />
      ))}
      <div ref={endRef} />
    </div>
  );
}

/** Champ de réponse : envoie dans le fil, affiché immédiatement. */
export function Composer({
  conversationId,
  placeholder,
  className,
  variant = "desktop",
}: {
  conversationId: string;
  placeholder?: string;
  className?: string;
  variant?: "desktop" | "mobile";
}) {
  const { can, isManagerView } = useSession();
  const send = useSendMessage(conversationId);
  const [text, setText] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  if (!can("messages.send")) return null;

  const submit = async () => {
    const body = text.trim();
    if (!body && files.length === 0) return;
    setError(null);
    try {
      const att = files.length ? await uploadFiles(files) : [];
      await send.mutateAsync({ body: body || "📎 Pièce jointe", attachmentIds: att.map((a) => a.id) });
      setText("");
      setFiles([]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Envoi impossible");
    }
  };

  return (
    <div className={className}>
      {files.length > 0 && (
        <div className="mb-2 text-[11.5px] text-muted">
          {files.map((f) => f.name).join(", ")}{" "}
          <button type="button" className="font-semibold text-danger" onClick={() => setFiles([])}>
            retirer
          </button>
        </div>
      )}
      <form
        className={cx("flex gap-2", variant === "mobile" && "items-center")}
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <input ref={fileRef} type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={(e) => setFiles([...(e.target.files ?? [])])} />
        {variant === "mobile" ? (
          // Style iOS : « + » rond, champ arrondi avec le bouton d'envoi intégré.
          <>
            <button
              type="button"
              aria-label="Joindre un fichier"
              onClick={() => fileRef.current?.click()}
              className="flex h-[34px] w-[34px] flex-shrink-0 items-center justify-center rounded-full bg-ios-fill text-[20px] text-muted"
            >
              +
            </button>
            <div className="flex min-w-0 flex-1 items-center rounded-[18px] border-[0.5px] border-ios-sep bg-white py-[3px] pl-3 pr-[3px]">
              <input
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={placeholder ?? (isManagerView ? "Répondre à la tête de réseau" : "Écrire à la boutique")}
                className="min-w-0 flex-1 bg-transparent text-[16px]"
              />
              <button
                type="submit"
                aria-label="Envoyer"
                disabled={send.isPending}
                className="flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-full bg-accent disabled:opacity-50"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M12 19V5M5 12l7-7 7 7" />
                </svg>
              </button>
            </div>
          </>
        ) : (
          <>
            <button
              type="button"
              aria-label="Joindre un fichier"
              onClick={() => fileRef.current?.click()}
              className="flex-shrink-0 rounded-full border border-line bg-white px-3 text-[15px] text-muted"
            >
              ＋
            </button>
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={placeholder ?? (isManagerView ? "Répondre à la tête de réseau…" : "Écrire à la boutique…")}
              className="min-w-0 flex-1 rounded-full border border-line bg-white px-[14px] py-[10px] text-[13px]"
            />
            <button
              type="submit"
              disabled={send.isPending}
              className="flex flex-shrink-0 items-center rounded-full bg-accent px-[18px] text-[12.5px] font-bold text-white disabled:opacity-50"
            >
              Envoyer
            </button>
          </>
        )}
      </form>
      {error && <div className="mt-1 text-[12px] font-semibold text-danger">{error}</div>}
    </div>
  );
}
